import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, Typography, TextField, Button, Paper, Grid, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  GetLobbyConfig, UpdateLobbyConfig,
  GetReasons, SaveReason, UpdateReason, DeleteReason,
  GetQuestions, SaveQuestion, UpdateQuestion, DeleteQuestion,
  GetQRCode, GetKioskDevices, ProvisionKioskDevice, RevokeKioskDevice
} from '../../../api';

const QUESTION_TYPES = [
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
];

// ── System Options ────────────────────────────────────────────────────────────
const SystemOptions = ({ access }) => {
  const [config, setConfig] = useState({ siteTitle: '', timezone: '', hoursNote: '', smsTemplate: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    GetLobbyConfig(access?.org?.id).then(c => c && setConfig(c)).catch(() => {});
  }, [access]);

  const save = () =>
    UpdateLobbyConfig(access?.org?.id, config).then(() => setSaved(true)).catch(() => {});

  return (
    <Paper sx={{ p: 3 }}>
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Saved.</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Site Title" value={config.siteTitle || ''} onChange={e => setConfig(c => ({ ...c, siteTitle: e.target.value }))} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Time Zone" value={config.timezone || ''} onChange={e => setConfig(c => ({ ...c, timezone: e.target.value }))} helperText="e.g. America/New_York" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Hours / Notes" multiline rows={2} value={config.hoursNote || ''} onChange={e => setConfig(c => ({ ...c, hoursNote: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="SMS Recall Template" multiline rows={2} value={config.smsTemplate || ''} onChange={e => setConfig(c => ({ ...c, smsTemplate: e.target.value }))} helperText="Use {name} for owner name" />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={save}>Save</Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

// ── Questions ─────────────────────────────────────────────────────────────────
const QuestionDialog = ({ open, initial, onSave, onClose }) => {
  const [form, setForm] = useState({ label: '', type: 'short_text', required: false, active: true });
  useEffect(() => { if (initial) setForm(initial); }, [initial]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial?.id ? 'Edit Question' : 'Add Question'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}><TextField fullWidth label="Label" value={form.label} onChange={e => set('label', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Type" value={form.type} onChange={e => set('type', e.target.value)}>
              {QUESTION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}><FormControlLabel control={<Checkbox checked={form.required} onChange={e => set('required', e.target.checked)} />} label="Required" /></Grid>
          <Grid item xs={6} sm={3}><FormControlLabel control={<Checkbox checked={form.active} onChange={e => set('active', e.target.checked)} />} label="Active" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const Questions = ({ access }) => {
  const [questions, setQuestions] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => GetQuestions(access?.org?.id).then(setQuestions).catch(() => {});
  useEffect(() => { load(); }, [access]); // eslint-disable-line

  const handleSave = async form => {
    if (form.id) await UpdateQuestion(form.id, access?.org?.id, form);
    else await SaveQuestion(access?.org?.id, form);
    setDialog(null);
    load();
  };

  const handleDelete = async id => {
    await DeleteQuestion(id, access?.org?.id);
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Questions</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setDialog({})}>Add</Button>
      </Box>
      <Table size="small" component={Paper}>
        <TableHead><TableRow>
          <TableCell>Label</TableCell><TableCell>Type</TableCell>
          <TableCell>Required</TableCell><TableCell>Active</TableCell><TableCell />
        </TableRow></TableHead>
        <TableBody>
          {questions.map(q => (
            <TableRow key={q.id}>
              <TableCell>{q.label}</TableCell>
              <TableCell>{q.type}</TableCell>
              <TableCell>{q.required ? 'Yes' : 'No'}</TableCell>
              <TableCell><Chip label={q.active ? 'Active' : 'Inactive'} size="small" color={q.active ? 'success' : 'default'} /></TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => setDialog(q)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => handleDelete(q.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <QuestionDialog open={!!dialog} initial={dialog} onSave={handleSave} onClose={() => setDialog(null)} />
    </Box>
  );
};

// ── Reasons ───────────────────────────────────────────────────────────────────
const ReasonDialog = ({ open, initial, onSave, onClose }) => {
  const [form, setForm] = useState({ label: '', active: true, subreasons: [] });
  const [newSub, setNewSub] = useState('');
  useEffect(() => { if (initial) setForm({ subreasons: [], ...initial }); }, [initial]);

  const addSub = () => {
    if (!newSub.trim()) return;
    setForm(f => ({ ...f, subreasons: [...f.subreasons, { label: newSub.trim(), active: true }] }));
    setNewSub('');
  };

  const removeSub = i => setForm(f => ({ ...f, subreasons: f.subreasons.filter((_, idx) => idx !== i) }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial?.id ? 'Edit Reason' : 'Add Reason'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}><TextField fullWidth label="Label" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} /></Grid>
          <Grid item xs={12}><FormControlLabel control={<Checkbox checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />} label="Active" /></Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Subreasons</Typography>
            {form.subreasons.map((s, i) => (
              <Chip key={i} label={s.label} onDelete={() => removeSub(i)} sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField size="small" label="Add subreason" value={newSub} onChange={e => setNewSub(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSub()} />
              <Button onClick={addSub}>Add</Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const Reasons = ({ access }) => {
  const [reasons, setReasons] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => GetReasons(access?.org?.id).then(setReasons).catch(() => {});
  useEffect(() => { load(); }, [access]); // eslint-disable-line

  const handleSave = async form => {
    if (form.id) await UpdateReason(form.id, access?.org?.id, form);
    else await SaveReason(access?.org?.id, form);
    setDialog(null);
    load();
  };

  const handleDelete = async id => { await DeleteReason(id, access?.org?.id); load(); };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Reasons &amp; Subreasons</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setDialog({})}>Add</Button>
      </Box>
      <Table size="small" component={Paper}>
        <TableHead><TableRow>
          <TableCell>Label</TableCell><TableCell>Subreasons</TableCell><TableCell>Active</TableCell><TableCell />
        </TableRow></TableHead>
        <TableBody>
          {reasons.map(r => (
            <TableRow key={r.id}>
              <TableCell>{r.label}</TableCell>
              <TableCell>{r.subreasons?.filter(s => s.active).map(s => s.label).join(', ') || '—'}</TableCell>
              <TableCell><Chip label={r.active ? 'Active' : 'Inactive'} size="small" color={r.active ? 'success' : 'default'} /></TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => setDialog(r)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ReasonDialog open={!!dialog} initial={dialog} onSave={handleSave} onClose={() => setDialog(null)} />
    </Box>
  );
};

// ── QR Code ───────────────────────────────────────────────────────────────────
const QRCode = ({ access }) => {
  const [qr, setQr] = useState(null);
  useEffect(() => {
    GetQRCode(access?.org?.id).then(setQr).catch(() => {});
  }, [access]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>QR Code</Typography>
      {qr ? (
        <Box>
          <img src={qr.dataUrl} alt="Check-in QR code" style={{ maxWidth: 256 }} />
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" href={qr.dataUrl} download="checkin-qr.png">Download</Button>
          </Box>
        </Box>
      ) : (
        <Typography color="text.secondary">QR code not available yet.</Typography>
      )}
    </Box>
  );
};

// ── Kiosk Devices ─────────────────────────────────────────────────────────────
const KioskDevices = ({ access }) => {
  const [devices, setDevices] = useState([]);
  const [name, setName] = useState('');
  const [newToken, setNewToken] = useState(null);

  const load = () => GetKioskDevices(access?.org?.id).then(setDevices).catch(() => {});
  useEffect(() => { load(); }, [access]); // eslint-disable-line

  const provision = async () => {
    if (!name.trim()) return;
    const result = await ProvisionKioskDevice(access?.org?.id, name.trim()).catch(() => null);
    if (!result) return;
    setNewToken(result);
    setName('');
    load();
  };

  const revoke = async id => {
    await RevokeKioskDevice(id, access?.org?.id);
    load();
  };

  const kioskUrl = newToken
    ? `${window.location.origin}/kiosk/${newToken.token}`
    : null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Kiosk Devices</Typography>
      {newToken && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNewToken(null)}>
          <Typography variant="body2" fontWeight={600}>Device provisioned — copy this URL now. It will not be shown again.</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-all' }}>{kioskUrl}</Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => navigator.clipboard?.writeText(kioskUrl)}>Copy URL</Button>
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField size="small" label="Device name" value={name} onChange={e => setName(e.target.value)} />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={provision}>Provision</Button>
      </Box>
      <Table size="small" component={Paper}>
        <TableHead><TableRow>
          <TableCell>Name</TableCell><TableCell>Last Seen</TableCell><TableCell>Status</TableCell><TableCell />
        </TableRow></TableHead>
        <TableBody>
          {devices.map(d => (
            <TableRow key={d.id}>
              <TableCell>{d.name}</TableCell>
              <TableCell>{d.lastSeen ? new Date(d.lastSeen).toLocaleString() : 'Never'}</TableCell>
              <TableCell><Chip label={d.revokedAt ? 'Revoked' : 'Active'} size="small" color={d.revokedAt ? 'default' : 'success'} /></TableCell>
              <TableCell>
                {!d.revokedAt && (
                  <IconButton size="small" color="error" onClick={() => revoke(d.id)}><DeleteIcon fontSize="small" /></IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!devices.length && <TableRow><TableCell colSpan={4} align="center">No devices provisioned</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Box>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────
const Configuration = ({ access }) => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Configuration</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="System Options" />
        <Tab label="Questions" />
        <Tab label="Reasons" />
        <Tab label="QR Code" />
        <Tab label="Kiosk Devices" />
      </Tabs>
      {tab === 0 && <SystemOptions access={access} />}
      {tab === 1 && <Questions access={access} />}
      {tab === 2 && <Reasons access={access} />}
      {tab === 3 && <QRCode access={access} />}
      {tab === 4 && <KioskDevices access={access} />}
    </Box>
  );
};

export default Configuration;
